import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { bedService, floorService, roomService, settingsService, tenantService } from '../services/api';
import { Bed, Floor, Room, User } from '../types';
import { formatFloorDisplay } from '../utils/locationLabels';
import { lookupAndPrefillTenantPhone, mergeTenantProfileFields } from '../utils/tenantPhonePrefill';
import './Dashboard.css';
import './CreateTenantProfile.css';

function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateStrToInput(v: string | undefined | null): string {
  if (v == null || v === '') return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return toYMD(d);
}

const formatFloorLine = formatFloorDisplay;

function formatRoomLine(r: Room): string {
  return `Room ${r.roomNumber}`;
}

function suggestRentForBed(bed: Bed, room: Room | undefined): number {
  const fromBed = bed.rentPerMonth ?? bed.rentPerWeek ?? bed.rentPerDay;
  const fromRoom = room?.rentPerBed;
  const n = fromBed ?? fromRoom ?? 0;
  return Number.isFinite(Number(n)) ? Number(n) : 0;
}

const CTP_ACCEPT_IMAGE_PDF = 'image/*,application/pdf,.pdf';

function isPdfFile(file: File): boolean {
  const t = (file.type || '').toLowerCase();
  if (t === 'application/pdf' || t === 'application/x-pdf') return true;
  return file.name.toLowerCase().endsWith('.pdf');
}

function isPdfUrl(url: string): boolean {
  const p = url.split(/[?#]/)[0]?.toLowerCase() ?? '';
  return p.endsWith('.pdf');
}

function CtpAssetPreview({
  label,
  file,
  blobUrl,
  remoteUrl,
}: {
  label: string;
  file: File | null;
  blobUrl: string | null;
  remoteUrl: string | null;
}) {
  const src = file ? blobUrl : remoteUrl;
  if (!src) return null;
  const asPdf = file ? isPdfFile(file) : isPdfUrl(src);
  if (asPdf) {
    return (
      <div className="ctp-file-preview ctp-file-preview--pdf">
        <iframe src={src} title={`${label} (PDF)`} className="ctp-pdf-frame" />
      </div>
    );
  }
  return (
    <div className="ctp-file-preview">
      <img src={src} alt={label} />
    </div>
  );
}

const emptyForm = (advanceDefault: string) => {
  const today = toYMD(new Date());
  return {
    username: '',
    phoneNumber: '',
    email: '',
    address: '',
    floorId: '' as number | '',
    roomId: '' as number | '',
    bedId: '' as number | '',
    rent: '',
    advance: advanceDefault,
    workingPlace: '',
    emergencyContactName: '',
    emergencyContactPhoneNumber: '',
    importantNote: '',
    dateOfJoining: today,
    paymentDate: today,
    vacatedDate: '',
    isActive: true,
  };
};

type FormState = ReturnType<typeof emptyForm>;

const CreateTenantProfile: React.FC = () => {
  const { hostel } = useAuth();
  const navigate = useNavigate();
  const editMatch = useMatch({ path: '/tenants/edit/:userId', end: true });
  const editUserIdRaw = editMatch?.params.userId;
  const editUserId = editUserIdRaw != null && editUserIdRaw !== '' ? parseInt(editUserIdRaw, 10) : NaN;
  const isEdit = Boolean(editMatch) && Number.isFinite(editUserId);
  const [searchParams] = useSearchParams();
  const checkInMode = isEdit && searchParams.get('checkIn') === '1';

  const [tenantDefaults, setTenantDefaults] = useState<{ hasDefault: boolean; defaultNewTenantAdvance: number } | null>(
    null
  );
  const [loadedTenant, setLoadedTenant] = useState<User | null>(null);
  const [pageReady, setPageReady] = useState(() => !isEdit);
  const [form, setForm] = useState<FormState>(() => emptyForm('0'));
  const [allFloors, setAllFloors] = useState<Floor[]>([]);
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [roomBeds, setRoomBeds] = useState<Bed[]>([]);
  const [floorSearch, setFloorSearch] = useState('');
  const [roomSearch, setRoomSearch] = useState('');
  const [floorListOpen, setFloorListOpen] = useState(false);
  const [roomListOpen, setRoomListOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [govIdFront, setGovIdFront] = useState<File | null>(null);
  const [govIdBack, setGovIdBack] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [phoneLookupHint, setPhoneLookupHint] = useState('');
  const floorBlurT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const roomBlurT = useRef<ReturnType<typeof setTimeout> | null>(null);

  const photoPreviewUrl = useMemo(
    () => (profilePhoto ? URL.createObjectURL(profilePhoto) : null),
    [profilePhoto]
  );
  const govIdFrontPreviewUrl = useMemo(
    () => (govIdFront ? URL.createObjectURL(govIdFront) : null),
    [govIdFront]
  );
  const govIdBackPreviewUrl = useMemo(
    () => (govIdBack ? URL.createObjectURL(govIdBack) : null),
    [govIdBack]
  );

  useEffect(() => {
    return () => {
      if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  useEffect(() => {
    return () => {
      if (govIdFrontPreviewUrl) URL.revokeObjectURL(govIdFrontPreviewUrl);
    };
  }, [govIdFrontPreviewUrl]);

  useEffect(() => {
    return () => {
      if (govIdBackPreviewUrl) URL.revokeObjectURL(govIdBackPreviewUrl);
    };
  }, [govIdBackPreviewUrl]);

  useEffect(() => {
    return () => {
      if (floorBlurT.current) clearTimeout(floorBlurT.current);
      if (roomBlurT.current) clearTimeout(roomBlurT.current);
    };
  }, []);

  useEffect(() => {
    if (!hostel?.id) return;
    if (editMatch && !Number.isFinite(editUserId)) {
      toast.error('Invalid tenant link');
      navigate('/tenants');
      return;
    }
    if (isEdit) {
      setPageReady(false);
      setLoadedTenant(null);
    }
    let cancelled = false;
    (async () => {
      try {
        const [d, floors, rooms] = await Promise.all([
          settingsService.getTenantDefaults(hostel.id),
          floorService.getFloorsByHostel(hostel.id),
          roomService.getRoomsByHostel(hostel.id),
        ]);
        if (cancelled) return;
        const adv = String(d.defaultNewTenantAdvance ?? 0);
        setTenantDefaults({
          hasDefault: d.hasDefault,
          defaultNewTenantAdvance: d.defaultNewTenantAdvance ?? 0,
        });
        const floorList = floors.filter((f) => f.isActive !== false);
        const roomList = rooms.filter((r) => r.isActive !== false);
        setAllFloors(floorList);
        setAllRooms(roomList);

        if (isEdit) {
          const t = await tenantService.getHostelTenant(hostel.id, editUserId);
          if (cancelled) return;
          setLoadedTenant(t);
          const fid = t.floor != null && String(t.floor).trim() !== '' ? Number(t.floor) : NaN;
          const rid = t.room != null && String(t.room).trim() !== '' ? Number(t.room) : NaN;
          const fl = Number.isFinite(fid) ? floorList.find((f) => f.id === fid) : undefined;
          const rm = Number.isFinite(rid) ? roomList.find((r) => r.id === rid) : undefined;
          const join = dateStrToInput(t.dateOfJoining);
          const pay = dateStrToInput(t.paymentDate);
          const today = toYMD(new Date());
          const activating = checkInMode && !t.isActive;
          setForm({
            username: t.username || '',
            phoneNumber: t.phoneNumber || '',
            email: t.email || '',
            address: t.address || '',
            floorId: Number.isFinite(fid) ? fid : '',
            roomId: Number.isFinite(rid) ? rid : '',
            bedId: t.bedId != null ? Number(t.bedId) : '',
            rent: t.rent != null && t.rent !== '' ? String(t.rent) : '',
            advance: t.advance != null && t.advance !== '' ? String(t.advance) : adv,
            workingPlace: t.workingPlace || '',
            emergencyContactName: t.emergencyContactName || '',
            emergencyContactPhoneNumber: t.emergencyContactPhoneNumber || '',
            importantNote: t.note || '',
            dateOfJoining: activating ? '' : join || today,
            paymentDate: activating ? '' : pay || today,
            vacatedDate: activating ? '' : dateStrToInput(t.dateOfLeft),
            isActive: activating ? true : t.isActive !== false,
          });
          setFloorSearch(fl ? formatFloorLine(fl) : '');
          setRoomSearch(rm ? formatRoomLine(rm) : '');
        } else {
          setLoadedTenant(null);
          setForm(emptyForm(adv));
          setFloorSearch('');
          setRoomSearch('');
        }
      } catch {
        if (!cancelled) {
          toast.error(isEdit ? 'Could not load tenant' : 'Could not load form data');
          if (isEdit) navigate('/tenants');
        }
      } finally {
        if (!cancelled) setPageReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hostel?.id, isEdit, editUserId, navigate, checkInMode]);

  useEffect(() => {
    if (form.roomId === '') {
      setRoomBeds([]);
      return;
    }
    const rid = Number(form.roomId);
    if (!Number.isFinite(rid)) return;
    const currentBedId = loadedTenant?.bedId != null ? Number(loadedTenant.bedId) : NaN;
    bedService
      .getBedsByRoom(rid)
      .then((beds) =>
        setRoomBeds(
          beds.filter(
            (b) => b.bedStatus === 'AVAILABLE' || (isEdit && Number.isFinite(currentBedId) && b.id === currentBedId)
          )
        )
      )
      .catch(() => {
        setRoomBeds([]);
        toast.error('Could not load beds for room');
      });
  }, [form.roomId, isEdit, loadedTenant?.bedId]);

  const roomsOnFloor = useMemo(() => {
    if (form.floorId === '') return [];
    const fid = Number(form.floorId);
    return allRooms.filter((r) => Number(r.floorId) === fid);
  }, [allRooms, form.floorId]);

  const filteredFloors = useMemo(() => {
    const s = floorSearch.trim().toLowerCase();
    let list = allFloors;
    if (s) {
      list = allFloors.filter((fl) => {
        const num = String(fl.floorNumber).toLowerCase();
        const name = (fl.floorName || '').toLowerCase();
        return num.includes(s) || name.includes(s) || String(fl.id) === s;
      });
    }
    return list.slice(0, 30);
  }, [allFloors, floorSearch]);

  const filteredRooms = useMemo(() => {
    if (form.floorId === '') return [];
    const s = roomSearch.trim().toLowerCase();
    let list = roomsOnFloor;
    if (s) {
      list = roomsOnFloor.filter((r) => String(r.roomNumber).toLowerCase().includes(s) || String(r.id) === s);
    }
    return list.slice(0, 30);
  }, [roomsOnFloor, roomSearch, form.floorId]);

  const openFloorList = () => {
    if (floorBlurT.current) clearTimeout(floorBlurT.current);
    setFloorListOpen(true);
  };

  const openRoomList = () => {
    if (roomBlurT.current) clearTimeout(roomBlurT.current);
    setRoomListOpen(true);
  };

  const scheduleCloseFloorList = () => {
    if (floorBlurT.current) clearTimeout(floorBlurT.current);
    floorBlurT.current = setTimeout(() => setFloorListOpen(false), 150);
  };

  const scheduleCloseRoomList = () => {
    if (roomBlurT.current) clearTimeout(roomBlurT.current);
    roomBlurT.current = setTimeout(() => setRoomListOpen(false), 150);
  };

  const selectFloor = (fl: Floor) => {
    setForm((f) => ({
      ...f,
      floorId: fl.id,
      roomId: '',
      bedId: '',
      rent: '',
    }));
    setFloorSearch(formatFloorLine(fl));
    setRoomSearch('');
    setFloorListOpen(false);
    setRoomListOpen(false);
  };

  const selectRoom = (r: Room) => {
    setForm((f) => ({
      ...f,
      roomId: r.id,
      bedId: '',
      rent: '',
    }));
    setRoomSearch(formatRoomLine(r));
    setRoomListOpen(false);
  };

  const handlePhoneLookup = async () => {
    if (!hostel?.id || isEdit) return;
    const phone = form.phoneNumber.trim();
    if (phone.replace(/\D/g, '').length < 8) return;
    setPhoneLookupHint('');
    const result = await lookupAndPrefillTenantPhone(hostel.id, phone, (profile, meta) => {
      if (meta.alreadyInHostel) return;
      setPhoneLookupHint(meta.hint || '');
      setForm((f) =>
        mergeTenantProfileFields(f, profile, [
          'username',
          'email',
          'address',
          'workingPlace',
          'emergencyContactName',
          'emergencyContactPhoneNumber',
        ]),
      );
    });
    if (result?.alreadyInHostel) {
      setPhoneLookupHint(result.hint || 'This user is already part of your property.');
    } else if (result && !result.found) {
      setPhoneLookupHint('');
    }
  };

  const handleBedChange = (bedIdStr: string) => {
    if (bedIdStr === '') {
      setForm((f) => ({ ...f, bedId: '', rent: '' }));
      return;
    }
    const bid = Number(bedIdStr);
    const bed = roomBeds.find((b) => b.id === bid);
    const room = allRooms.find((r) => r.id === Number(form.roomId));
    const suggested = bed ? suggestRentForBed(bed, room) : 0;
    setForm((f) => ({
      ...f,
      bedId: bid,
      rent: String(suggested),
    }));
  };

  const appendIf = (fd: FormData, key: string, value: string | undefined) => {
    if (value !== undefined && value !== '') fd.append(key, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hostel?.id) return;
    if (!isEdit && !tenantDefaults?.hasDefault) {
      toast.error('Configure a default tenant password under Settings before creating profiles.');
      return;
    }
    if (!form.username.trim() || !form.phoneNumber.trim()) {
      toast.error('Full name and phone are required.');
      return;
    }
    const bedRequired = !isEdit || form.isActive;
    if (bedRequired && (form.floorId === '' || form.roomId === '' || form.bedId === '')) {
      toast.error('Floor, room, and bed are required for active tenants.');
      return;
    }
    if (!form.dateOfJoining?.trim() || !form.paymentDate?.trim()) {
      toast.error('Date of joining and payment date are required.');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('username', form.username.trim());
      fd.append('phoneNumber', form.phoneNumber.trim());
      appendIf(fd, 'email', form.email.trim());
      fd.append('address', form.address.trim());
      fd.append('workingPlace', form.workingPlace.trim());
      fd.append('emergencyContactName', form.emergencyContactName.trim());
      fd.append('emergencyContactPhoneNumber', form.emergencyContactPhoneNumber.trim());
      fd.append('note', form.importantNote.trim());
      fd.append('dateOfJoining', form.dateOfJoining);
      fd.append('paymentDate', form.paymentDate);
      appendIf(fd, 'dateOfLeft', form.vacatedDate);
      fd.append('rent', String(Number(form.rent) || 0));
      fd.append('advance', String(Number(form.advance) || 0));
      if (isEdit) {
        fd.append('isActive', form.isActive ? 'true' : 'false');
        if (form.isActive && form.bedId !== '') {
          fd.append('bedId', String(form.bedId));
        }
      } else {
        fd.append('bedId', String(form.bedId));
      }
      if (profilePhoto) fd.append('photo', profilePhoto);
      if (govIdFront) fd.append('idFrontSide', govIdFront);
      if (govIdBack) fd.append('idBackSide', govIdBack);

      if (isEdit) {
        await tenantService.updateTenant(hostel.id, editUserId, fd);
        toast.success(checkInMode && form.isActive ? 'Tenant checked in.' : 'Profile updated.');
      } else {
        await tenantService.createTenant(hostel.id, fd);
        toast.success('Profile created. Tenant can sign in with phone and the default password from Settings.');
      }
      navigate('/tenants');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || (isEdit ? 'Failed to update tenant' : 'Failed to create tenant'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!hostel) return <div className="loading-container">Loading...</div>;
  if (editMatch && !Number.isFinite(editUserId)) {
    return <div className="loading-container">Redirecting…</div>;
  }
  if (isEdit && !pageReady) return <div className="loading-container">Loading profile…</div>;

  const bedRequired = !isEdit || form.isActive;
  const pageTitle = checkInMode ? 'Check in tenant' : isEdit ? 'Edit profile' : 'Create Profile';

  return (
    <div className="dashboard-container ctp-page">
      <button type="button" className="ctp-back" onClick={() => navigate('/tenants')}>
        ← Tenants
      </button>

      <div className="ctp-card">
        <h1 className="ctp-title">{pageTitle}</h1>
        <p className="ctp-sub">
          {checkInMode
            ? 'Assign floor, room, and bed, keep Active checked, then save to check this tenant back in.'
            : isEdit
              ? 'Update tenant details. Use Active when they are staying; uncheck to mark inactive (bed is released). Floor, room, and bed are required only while active.'
              : 'Full name, phone, floor, room, bed, date of joining, and payment date are required. Email is optional. New tenants use the default password from Settings to sign in. Rent can pre-fill when you pick a bed.'}
        </p>

        {checkInMode ? (
          <p className="ctp-lookup-banner">
            Check-in mode — Active is pre-selected. Pick an available bed, set a new date of joining and payment date, then save.
          </p>
        ) : null}

        <form onSubmit={handleSubmit}>
          {isEdit ? (
            <label className="ctp-active-row">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => {
                  const next = e.target.checked;
                  setForm((f) => ({
                    ...f,
                    isActive: next,
                    ...(next ? { vacatedDate: '' } : {}),
                  }));
                }}
              />
              <span>
                <strong>Active</strong>
                <span className="ctp-active-row__hint">
                  {form.isActive ? 'Tenant is currently staying' : 'Inactive — no bed required until checked in'}
                </span>
              </span>
            </label>
          ) : null}
          <div className="ctp-grid ctp-grid--2">
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-name">
                Full Name <span className="ctp-req-star">*</span>
              </label>
              <input
                id="ctp-name"
                className="ctp-input"
                required
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              />
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-phone">
                Phone Number <span className="ctp-req-star">*</span>
              </label>
              <input
                id="ctp-phone"
                className="ctp-input"
                required
                type="tel"
                value={form.phoneNumber}
                onChange={(e) => {
                  setPhoneLookupHint('');
                  setForm((f) => ({ ...f, phoneNumber: e.target.value }));
                }}
                onBlur={() => void handlePhoneLookup()}
              />
              {!isEdit ? (
                <span className="ctp-field-hint">Tab out after phone to load details from a prior registration.</span>
              ) : null}
            </div>
          </div>

          {phoneLookupHint && !isEdit ? (
            <p
              className={`ctp-lookup-banner${
                phoneLookupHint.toLowerCase().includes('already part') ? ' ctp-lookup-banner--warn' : ''
              }`}
            >
              {phoneLookupHint}
            </p>
          ) : null}

          <div className="ctp-grid ctp-grid--2">
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-email">
                Email
              </label>
              <input
                id="ctp-email"
                type="email"
                className="ctp-input"
                placeholder="Optional"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-address">
                Address
              </label>
              <input
                id="ctp-address"
                className="ctp-input"
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              />
            </div>
          </div>

          <div className="ctp-grid ctp-grid--3">
            <div className="ctp-field ctp-field--combo">
              <span className="ctp-label" id="ctp-floor-lbl">
                Floor {bedRequired ? <span className="ctp-req-star">*</span> : null}
              </span>
              <input
                className="ctp-input"
                autoComplete="off"
                placeholder="Floor"
                aria-labelledby="ctp-floor-lbl"
                aria-required="true"
                value={floorSearch}
                onChange={(e) => {
                  setFloorSearch(e.target.value);
                  setRoomSearch('');
                  openFloorList();
                  setForm((f) => ({
                    ...f,
                    floorId: '',
                    roomId: '',
                    bedId: '',
                    rent: '',
                  }));
                }}
                onFocus={openFloorList}
                onBlur={scheduleCloseFloorList}
              />
              {floorListOpen && (
                <ul className="ctp-suggest-list">
                  {filteredFloors.length === 0 ? (
                    <li style={{ padding: '0.5rem', color: '#888', fontSize: '0.9rem' }}>No matching floors</li>
                  ) : (
                    filteredFloors.map((fl) => (
                      <li key={fl.id}>
                        <button
                          type="button"
                          onMouseDown={(ev) => {
                            ev.preventDefault();
                            selectFloor(fl);
                          }}
                        >
                          {formatFloorLine(fl)}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
            <div className="ctp-field ctp-field--combo">
              <span className="ctp-label" id="ctp-room-lbl">
                Room {bedRequired ? <span className="ctp-req-star">*</span> : null}
              </span>
              <input
                className="ctp-input"
                autoComplete="off"
                placeholder="Room"
                disabled={form.floorId === ''}
                aria-labelledby="ctp-room-lbl"
                aria-required="true"
                value={roomSearch}
                onChange={(e) => {
                  setRoomSearch(e.target.value);
                  openRoomList();
                  setForm((f) => ({
                    ...f,
                    roomId: '',
                    bedId: '',
                    rent: '',
                  }));
                }}
                onFocus={() => {
                  if (form.floorId !== '') openRoomList();
                }}
                onBlur={scheduleCloseRoomList}
              />
              {roomListOpen && form.floorId !== '' && (
                <ul className="ctp-suggest-list">
                  {filteredRooms.length === 0 ? (
                    <li style={{ padding: '0.5rem', color: '#888', fontSize: '0.9rem' }}>No matching rooms</li>
                  ) : (
                    filteredRooms.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          onMouseDown={(ev) => {
                            ev.preventDefault();
                            selectRoom(r);
                          }}
                        >
                          {formatRoomLine(r)}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-bed">
                Bed {bedRequired ? <span className="ctp-req-star">*</span> : null}
              </label>
              <select
                id="ctp-bed"
                className="ctp-input"
                required={bedRequired}
                disabled={form.roomId === ''}
                value={form.bedId === '' ? '' : String(form.bedId)}
                onChange={(e) => handleBedChange(e.target.value)}
              >
                <option value="">Bed</option>
                {roomBeds.map((b) => (
                  <option key={b.id} value={b.id}>
                    Bed {b.bedNumber}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="ctp-grid ctp-grid--2">
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-work">
                Working Place and Address
              </label>
              <input
                id="ctp-work"
                className="ctp-input"
                value={form.workingPlace}
                onChange={(e) => setForm((f) => ({ ...f, workingPlace: e.target.value }))}
              />
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-em-name">
                Emergency Contact Name
              </label>
              <input
                id="ctp-em-name"
                className="ctp-input"
                value={form.emergencyContactName}
                onChange={(e) => setForm((f) => ({ ...f, emergencyContactName: e.target.value }))}
              />
            </div>
          </div>

          <div className="ctp-grid ctp-grid--2">
            <div className="ctp-field ctp-field--full">
              <label className="ctp-label" htmlFor="ctp-em-phone">
                Emergency Contact Number
              </label>
              <input
                id="ctp-em-phone"
                className="ctp-input"
                value={form.emergencyContactPhoneNumber}
                onChange={(e) => setForm((f) => ({ ...f, emergencyContactPhoneNumber: e.target.value }))}
              />
            </div>
          </div>

          <div className="ctp-grid ctp-grid--2">
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-join">
                Date of Joining <span className="ctp-req-star">*</span>
              </label>
              <input
                id="ctp-join"
                type="date"
                required
                className="ctp-input"
                value={form.dateOfJoining}
                onChange={(e) => setForm((f) => ({ ...f, dateOfJoining: e.target.value }))}
              />
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-pay">
                Payment Date <span className="ctp-req-star">*</span>
              </label>
              <input
                id="ctp-pay"
                type="date"
                required
                className="ctp-input"
                value={form.paymentDate}
                onChange={(e) => setForm((f) => ({ ...f, paymentDate: e.target.value }))}
              />
            </div>
          </div>

          <div className="ctp-grid ctp-grid--3">
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-vacated">
                Vacated Date
              </label>
              <input
                id="ctp-vacated"
                type="date"
                className="ctp-input"
                value={form.vacatedDate}
                onChange={(e) => setForm((f) => ({ ...f, vacatedDate: e.target.value }))}
              />
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-rent">
                Rent
              </label>
              <input
                id="ctp-rent"
                type="number"
                min={0}
                step="1"
                className="ctp-input"
                value={form.rent}
                onChange={(e) => setForm((f) => ({ ...f, rent: e.target.value }))}
              />
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-adv">
                Advance
              </label>
              <input
                id="ctp-adv"
                type="number"
                min={0}
                step="1"
                className="ctp-input"
                value={form.advance}
                onChange={(e) => setForm((f) => ({ ...f, advance: e.target.value }))}
              />
            </div>
          </div>

          <div className="ctp-grid ctp-grid--3">
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-photo">
                Profile Photo
              </label>
              <div className="ctp-upload-row">
                <input
                  id="ctp-photo"
                  type="file"
                  accept={CTP_ACCEPT_IMAGE_PDF}
                  className="ctp-file ctp-file--flex"
                  onChange={(e) => setProfilePhoto(e.target.files?.[0] ?? null)}
                />
                <CtpAssetPreview
                  label="Profile photo preview"
                  file={profilePhoto}
                  blobUrl={photoPreviewUrl}
                  remoteUrl={!profilePhoto && loadedTenant?.photo ? loadedTenant.photo : null}
                />
              </div>
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-idf">
                Gov ID Front
              </label>
              <div className="ctp-upload-row">
                <input
                  id="ctp-idf"
                  type="file"
                  accept={CTP_ACCEPT_IMAGE_PDF}
                  className="ctp-file ctp-file--flex"
                  onChange={(e) => setGovIdFront(e.target.files?.[0] ?? null)}
                />
                <CtpAssetPreview
                  label="ID front preview"
                  file={govIdFront}
                  blobUrl={govIdFrontPreviewUrl}
                  remoteUrl={!govIdFront && loadedTenant?.idFrontSide ? loadedTenant.idFrontSide : null}
                />
              </div>
            </div>
            <div className="ctp-field">
              <label className="ctp-label" htmlFor="ctp-idb">
                Gov ID Back
              </label>
              <div className="ctp-upload-row">
                <input
                  id="ctp-idb"
                  type="file"
                  accept={CTP_ACCEPT_IMAGE_PDF}
                  className="ctp-file ctp-file--flex"
                  onChange={(e) => setGovIdBack(e.target.files?.[0] ?? null)}
                />
                <CtpAssetPreview
                  label="ID back preview"
                  file={govIdBack}
                  blobUrl={govIdBackPreviewUrl}
                  remoteUrl={!govIdBack && loadedTenant?.idBackSide ? loadedTenant.idBackSide : null}
                />
              </div>
            </div>
          </div>

          <div className="ctp-grid ctp-grid--2">
            <div className="ctp-field ctp-field--full">
              <label className="ctp-label" htmlFor="ctp-note">
                Important Note
              </label>
              <textarea
                id="ctp-note"
                className="ctp-input ctp-textarea"
                rows={5}
                placeholder="Optional — allergies, preferences, special instructions…"
                value={form.importantNote}
                onChange={(e) => setForm((f) => ({ ...f, importantNote: e.target.value }))}
              />
            </div>
          </div>

          <div className="ctp-actions">
            <button type="submit" className="ctp-submit" disabled={submitting}>
              {submitting
                ? isEdit
                  ? 'Saving…'
                  : 'Creating…'
                : checkInMode
                  ? 'Check in'
                  : isEdit
                    ? 'Save changes'
                    : 'Create Profile'}
            </button>
            <button type="button" className="ctp-cancel" disabled={submitting} onClick={() => navigate('/tenants')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTenantProfile;
